import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { adminSendComposedEmail } from "@/lib/api/compose-email.functions";
import { adminListEmailAudience } from "@/lib/api/email-audience.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail, Paperclip, Send, Users, X } from "lucide-react";


export const Route = createFileRoute("/_authenticated/portal/admin/compose-email")({
  head: () => ({
    meta: [
      { title: "Compose email — Admin | Nkadimeng Danny Legal & Compliance" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ComposeEmailPage,
});

const SENDERS = [
  "info@nkadimengdannylcs.com",
  "admin@nkadimengdannylcs.com",
  "noreply@nkadimengdannylcs.com",
];

const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface Attachment {
  filename: string;
  contentBase64: string;
  contentType?: string;
  size: number;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function ComposeEmailPage() {
  const sendFn = useServerFn(adminSendComposedEmail);
  const audienceFn = useServerFn(adminListEmailAudience);

  const [from, setFrom] = useState(SENDERS[0]);
  const [senderName, setSenderName] = useState("Nkadimeng Danny Legal & Compliance");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [reference, setReference] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [audience, setAudience] = useState<"cv" | "all_clients">("cv");
  const [showAudience, setShowAudience] = useState(false);
  const [results, setResults] = useState<
    Array<{ email: string; ok: boolean; error?: string }>
  >([]);

  const audienceQuery = useQuery({
    queryKey: ["email-audience", audience],
    queryFn: () => audienceFn({ data: { audience } } as never),
    enabled: showAudience,
  });

  const recipientLines = to.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
  const recipientCount = recipientLines.length;

  function addRecipients(entries: string[]) {
    const existing = new Set(
      recipientLines.map((l) => (l.match(/<([^>]+)>/)?.[1] ?? l).trim().toLowerCase()),
    );
    const next = entries.filter((e) => {
      const email = (e.match(/<([^>]+)>/)?.[1] ?? e).trim().toLowerCase();
      if (existing.has(email)) return false;
      existing.add(email);
      return true;
    });
    if (next.length === 0) {
      toast.info("All of those recipients are already on the list");
      return;
    }
    setTo((prev) => [...recipientLines, ...next].join("\n") || prev);
    toast.success(`Added ${next.length} recipient${next.length === 1 ? "" : "s"}`);
  }

  const send = useMutation({
    mutationFn: async () => {
      // The send API accepts 50 recipients per call — batch bigger audiences.
      const batches: string[][] = [];
      for (let i = 0; i < recipientLines.length; i += 50) {
        batches.push(recipientLines.slice(i, i + 50));
      }
      const all: Array<{ email: string; ok: boolean; error?: string }> = [];
      for (const batch of batches) {
        const r: any = await sendFn({
          data: {
            from,
            senderName,
            to: batch.join("\n"),
            cc: batches.length > 1 ? "" : cc,
            bcc: batches.length > 1 ? "" : bcc,
            replyTo,
            subject,
            body,
            reference,
            attachments: attachments.map(({ filename, contentBase64, contentType }) => ({
              filename,
              contentBase64,
              contentType,
            })),
          } as never,
        });
        all.push(...(r?.results ?? []));
      }
      return { results: all };
    },

    onSuccess: (r: any) => {
      setResults(r.results);
      const ok = r.results.filter((x: any) => x.ok).length;
      const fail = r.results.length - ok;
      if (ok) toast.success(`Sent ${ok} email${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
      else toast.error(r.results[0]?.error ?? "Send failed");
      if (ok && !fail) {
        setSubject("");
        setBody("");
        setAttachments([]);
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "Send failed"),
  });

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    const next: Attachment[] = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} is larger than 10 MB`);
        continue;
      }
      next.push({
        filename: file.name,
        contentBase64: await fileToBase64(file),
        contentType: file.type || "application/octet-stream",
        size: file.size,
      });
    }
    setAttachments((prev) => [...prev, ...next].slice(0, 5));
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl text-navy">
          <Mail className="h-6 w-6" /> Compose email
        </h1>
        <p className="text-sm text-muted-foreground">
          Send a branded email from info@ or admin@ with attachments. Every send is logged in the
          Sent emails audit.
        </p>
      </div>

      <div className="grid gap-4 rounded-2xl border border-border bg-card p-5">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Send from</Label>
            <select
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {SENDERS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Display name</Label>
            <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-muted/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-navy">
                <Users className="h-4 w-4" /> Invite a saved audience
              </p>
              <p className="text-xs text-muted-foreground">
                Load everyone you've emailed about CVs, ATS scans and careers — great for seminar,
                webinar or career-session invitations.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAudience((v) => !v)}
            >
              {showAudience ? "Hide list" : "Load audience"}
            </Button>
          </div>

          {showAudience && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={audience}
                  onChange={(e) => setAudience(e.target.value as "cv" | "all_clients")}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  aria-label="Audience"
                >
                  <option value="cv">CV / ATS / career contacts</option>
                  <option value="all_clients">Everyone we've emailed</option>
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={audienceQuery.isLoading || !audienceQuery.data?.contacts?.length}
                  onClick={() =>
                    addRecipients(
                      (audienceQuery.data?.contacts ?? []).map((c: any) =>
                        c.name ? `${c.name} <${c.email}>` : c.email,
                      ),
                    )
                  }
                >
                  Add all ({audienceQuery.data?.total ?? 0})
                </Button>
                {audienceQuery.isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              <div className="max-h-52 overflow-y-auto rounded-md border border-border bg-background">
                {(audienceQuery.data?.contacts ?? []).length === 0 && !audienceQuery.isLoading ? (
                  <p className="p-3 text-xs text-muted-foreground">No contacts found yet.</p>
                ) : (
                  <ul className="divide-y divide-border text-xs">
                    {(audienceQuery.data?.contacts ?? []).map((c: any) => (
                      <li key={c.email} className="flex items-center gap-2 px-3 py-1.5">
                        <span className="truncate">{c.name ? `${c.name} — ` : ""}{c.email}</span>
                        <span className="ml-auto shrink-0 text-muted-foreground">{c.source}</span>
                        <button
                          type="button"
                          className="shrink-0 text-navy underline"
                          onClick={() =>
                            addRecipients([c.name ? `${c.name} <${c.email}>` : c.email])
                          }
                        >
                          Add
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <Label>To</Label>
          <Textarea
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={"One per line. e.g.\nJane Doe <jane@acme.co.za>\nclient@example.com"}
            className="min-h-[90px] font-mono text-xs"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            {recipientCount} recipient{recipientCount === 1 ? "" : "s"} · sent automatically in
            batches of 50
          </p>
        </div>


        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <Label>CC (optional)</Label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="a@b.com, c@d.com" />
          </div>
          <div>
            <Label>BCC (optional)</Label>
            <Input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="a@b.com" />
          </div>
          <div>
            <Label>Reply-to (optional)</Label>
            <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder={from} />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Reference (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="NDL-0001" />
          </div>
        </div>

        <div>
          <Label>Message</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="min-h-[220px]"
            placeholder="Write your message. Line breaks are preserved in the branded template."
          />
        </div>

        <div>
          <Label>Attachments (max 5, 10 MB each)</Label>
          <label className="mt-1 flex w-fit cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-accent">
            <Paperclip className="h-4 w-4" /> Add files
            <input type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          </label>
          {attachments.length > 0 && (
            <ul className="mt-2 space-y-1">
              {attachments.map((a, i) => (
                <li key={`${a.filename}-${i}`} className="flex items-center gap-2 text-xs">
                  <span className="truncate">{a.filename}</span>
                  <span className="text-muted-foreground">{(a.size / 1024).toFixed(0)} KB</span>
                  <button
                    type="button"
                    onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${a.filename}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => send.mutate()}
            disabled={send.isPending || !subject.trim() || !body.trim() || recipientCount === 0}
            className="bg-navy hover:bg-navy/90"
          >
            {send.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send email
          </Button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg text-navy">Last send</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {results.map((r) => (
              <li key={r.email} className="flex flex-wrap items-center gap-2">
                <span className={r.ok ? "text-emerald-600" : "text-red-600"}>
                  {r.ok ? "Sent" : "Failed"}
                </span>
                <span>{r.email}</span>
                {r.error && <span className="text-xs text-muted-foreground">{r.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
